/**
 * K1–K12 — DEGISMEZ IS KURALLARI.
 *
 * Kaynak: MOD_ERP_DEVIR_DOKUMANI.md Bolum 3.4. Devir dokumani Bolum 13'te bu 12 kuralin
 * "kabul kriteri (acceptance criteria) olarak yazilmasi" isteniyor — `rules.test.ts` tam olarak odur.
 *
 * MIMARI ILKE: bu fonksiyonlar SAFTIR ve hem client hem server tarafindan cagrilir.
 *   - client'ta: butonu sondurur, gerekcesini kullaniciya gosterir
 *   - server'da: istegi REDDEDER (T6, T7 — "sistem seviyesinde zorlanmali")
 * Ayni kuralin iki yerde ayri yazilmasi bu projede en pahali hatadir; tek kaynak burasi.
 */
import { PHOTO_STEP_PHASE, SEF_YETKILI_ROLLER, } from './enums.js';
import { olcuTipi } from './measurement.js';
export const OK = { ok: true };
export function fail(...violations) {
    return { ok: false, violations };
}
export function combine(...results) {
    const violations = results.flatMap((r) => (r.ok ? [] : r.violations));
    return violations.length ? { ok: false, violations } : OK;
}
/**
 * "Sef yetkisi" tek tanim, tek yer.
 *
 * Admin, sefin yapabildigi her seyi yapabilir. Bu ust-kume iliskisini her kural
 * fonksiyonunda ayri ayri yazmak, ileride birinin unutulmasi demektir.
 *
 * ⚠️ TEMSIL sirasinda `sistemRol` HEDEF kullanicinindir; yani admin bir formen'i
 *    temsil ederken buradan `false` doner ve is onaylayamaz (K1). Kasitlidir.
 */
export function sefYetkisiVarMi(actor) {
    /*
     * ⚠️ MUSTERI YETKILISI HICBIR KOSULDA SEF SAYILMAZ — `sistemRol` admin olsa
     *    bile. Rol atama kurali zaten musteriye admin verilmesini reddediyor
     *    ama kapi burada da tutuluyor: veriye elle yazilmis bir satir ya da
     *    ileride eklenecek bir yol bu kontrolu atlatabilir. Musteri, kendi
     *    santiyesinin panosunu OKUR; onaylamaz, acmaz, yonetmez.
     */
    if (musteriYetkilisiMi(actor))
        return false;
    return (actor.sistemRol === 'admin' ||
        SEF_YETKILI_ROLLER.includes(actor.role));
}
/**
 * MUSTERI TARAFINDAKI IZLEYICI mi (Nuh/Akcansa yetkilisi)?
 *
 * ⚠️ SISTEM ROLUNE BAKILMAZ, OPERASYONEL ROLE BAKILIR. Temsil sirasinda rol
 *    hedefinkidir; bir musteri yetkilisini temsil eden admin de bu kapinin
 *    ic tarafinda kalir (yetki yukseltme araci olmasin diye).
 */
export function musteriYetkilisiMi(actor) {
    return actor.role === 'musteri';
}
/**
 * MUSTERI YETKILISININ GOREBILECEGI SAYFALAR.
 *
 * Kullanici karari (07.09.2026): pano ("bugun dikkat gerektiren" HARIC),
 * gunluk rapor, kurulu iskeleler, arama & arsiv. Buna iki sey eklendi:
 *   · `/hesabim` — kendi parolasini/e-postasini degistiremeyen bir hesap
 *     yonetilemez; sayfa zaten yalniz kisinin KENDI kaydini acar.
 *   · `/is/<id>` — kurulu iskele ve arsiv listeleri satira tiklayinca ise
 *     gidiyor (kullanici karari: "evet, salt okunur"). Bu yol olmasaydi iki
 *     sayfa da yarim kalirdi: musteri 40 gundur ayakta bir iskeleyi gorur
 *     ama neye ait oldugunu acamazdi.
 *
 * ⚠️ LISTE BEYAZ LISTEDIR. Yeni bir ekran eklendiginde musteriye KAPALI
 *    baslar; acmak bilincli bir karar olur. Kara liste olsaydi her yeni
 *    ekran sessizce musteriye acilirdi.
 */
export const MUSTERI_SAYFALARI = ['/', '/rapor', '/kurulu', '/arsiv', '/hesabim'];
export function musteriSayfasiMi(yol) {
    const temiz = yol.split('?')[0] ?? '/';
    if (MUSTERI_SAYFALARI.includes(temiz))
        return true;
    // Is detayi — salt okunur.
    return temiz.startsWith('/is/');
}
/**
 * Bir adimin GECERLI fotograf sayisi.
 *
 * ⚠️ GECERSIZ ISARETLENENLER SAYILMAZ. Tek yerde suzuluyor: K3, K5 ve oteki
 *    gecitlerin hepsi bu fonksiyondan geciyor, ayrisma riski yok.
 */
const photoCount = (job, step) => job.photos.filter((p) => p.step === step && !p.gecersiz).length;
/* ------------------------------------------------------------------ */
/* K1 — Kuran kisi kendi isini onaylayamaz                              */
/* ------------------------------------------------------------------ */
/**
 * K1'IN KIMLIK KONTROLU — "bu kisi ise KATILDI mi".
 *
 * ⚠️ CALISAN ID'SI ILE KARSILASTIRILIR. `ekip` / `kesifciId` hep CALISAN id'sidir;
 *    `actor.id` ise User.id'dir ve ikisi ASLA esitlenmez. K1 uzun sure `actor.id`
 *    kullandigi icin *"kuran kisi kendi isini onaylayamaz"* dali sahada HIC
 *    atesnlenmedi — kural kodda vardi, uygulamada yoktu (05.08.2026'da olculdu).
 *
 * ⚠️ `eid` BOS ISE HICBIR SEYLE ESLESMEZ. Bu sart sus degil: uretimde 997 isin
 *    `kesifciId`'si NULL (Excel'den goc etmis tarihsel kayitlar). Korumasiz
 *    yazilsaydi `null === null` dogru cikar ve o islerin TESLIMI KILITLENIRDI —
 *    kuralin hic ilgilenmedigi bir durumda.
 *
 * ⚠️ TEMSIL DE KAPSANIR (`gercekEmployeeId`). Aksi halde kural sudan bir yolla
 *    asilirdi: isi kuran admin, baska bir sefin YERINE GECIP onaylar. Bu bir
 *    yetki YUKSELTMESI degil ama K1 acisindan farksiz — regulasyonun engellemek
 *    istedigi tam olarak "isi yapan onayladi" durumudur ve tiklayanin ekranda
 *    hangi ismi tasidigi bunu degistirmez. Iz kaydinda gorunur olmasi yetmez:
 *    "sonradan fark edilir" ile "engellenir" ayni sey degildir.
 */
function iseKatildiMi(job, actor) {
    const kimlikler = [actor.employeeId, actor.gercekEmployeeId].filter((x) => !!x);
    return kimlikler.some((eid) => job.ekip.includes(eid) || job.kesifciId === eid);
}
/**
 * REGULASYON. Onayi 3. kisi (sef/muhendis) verir.
 * Formen isi GOREBILIR ama ONAYLAYAMAZ — bu yuzden rol kontrolu de var.
 */
export function k1_onaylayabilirMi(job, actor) {
    const violations = [];
    if (!sefYetkisiVarMi(actor)) {
        violations.push({
            code: 'K1',
            message: 'Kurulumu yalnızca şantiye şefi (veya admin) onaylayabilir. Formen görebilir, onaylayamaz.',
        });
    }
    if (iseKatildiMi(job, actor)) {
        violations.push({
            code: 'K1',
            /*
             * ⚠️ MESAJ NE YAPILACAGINI SOYLER. Onceki hali ("onayi ucuncu bir kisi
             *    vermelidir") sefe ekranda BOZUK bir sistem gibi gorunurdu: butun
             *    kosullar saglanmis, dugme calismiyor. Sebep + cikis yolu birlikte.
             */
            message: 'Bu işin keşfini ya da kurulumunu siz yaptınız; kendi işinizi onaylayamazsınız (K1, regülasyon). Onayı başka bir şantiye şefi vermelidir.',
        });
    }
    return violations.length ? { ok: false, violations } : OK;
}
/* ------------------------------------------------------------------ */
/* İŞE DAHİL Mİ — bir işin adımlarını yürütme yetkisi                    */
/* ------------------------------------------------------------------ */
/**
 * Bu kullanıcı BU İŞİN adımlarını yürütebilir mi?
 *
 * Kullanıcı kararı (04.08.2026): atanmamış bir kullanıcı başka bir işin
 * adımlarını yürütemez; **yalnız şantiye şefleri** (şef yetkisi) her işi
 * yürütebilir. Bu, denetimde bulunan IDOR sınıfını kapatır — önceden herhangi
 * bir oturumlu kullanıcı herhangi bir işin keşfini/kurulumunu/malzemesini
 * işleyebiliyordu.
 *
 * "Dahil" = işin kurulum ekibinde, keşifçisi ya da ekip başı. Keşifçi iş
 * AÇILIRKEN atandığı için (§, is.service kesifciId) taze bir işin bile bir
 * katılımcısı vardır: o keşifçi keşfi yapabilir, başkası yapamaz.
 *
 * ⚠️ "Şantiye şefleri" = `sefYetkisiVarMi` (şef · mühendis · admin). Temsil
 *    sırasında `sistemRol` hedefinkidir, yani admin bir ustayı temsil ederken
 *    bu yetkiyi KAYBEDER — kasıtlı (yetki yükseltme aracı olamaz).
 *
 * ⚠️ Bu, işi ONAYLAMA (K1) değildir. Şef her işi YÜRÜTEBİLİR ama kuran kişi
 *    kendi işini yine ONAYLAYAMAZ (K1 ayrı kapı, teslimde).
 */
export function isYurutebilirMi(job, actor) {
    /*
     * ⚠️ MUSTERI YETKILISI HICBIR ADIMI YURUTEMEZ — ekip listesinde gorunse
     *    bile. Musteri tarafindaki kisi Mod'un kadrosu degil; ona bir ise
     *    "atanmis" olmak bir yetki vermez. Kapi burada, cunku fotograf yukleme
     *    de dahil butun eylem uclari bu fonksiyondan geciyor.
     */
    if (musteriYetkilisiMi(actor)) {
        return fail({
            code: 'YETKI',
            message: 'Müşteri yetkilisi iş adımlarını yürütemez — panel salt okunurdur.',
        });
    }
    if (sefYetkisiVarMi(actor))
        return OK;
    // ⚠️ ÇALIŞAN id'si — `ekip`/`kesifciId`/`ekipBasiId` hep çalışan id'sidir,
    //    User.id (`actor.id`) ile karşılaştırmak asla eşleşmezdi.
    const eid = actor.employeeId;
    if (eid && (job.ekip.includes(eid) || job.kesifciId === eid || job.ekipBasiId === eid)) {
        return OK;
    }
    return fail({
        code: 'YETKI',
        message: 'Bu işe atanmadınız — yalnız kendi işlerinizin adımlarını yürütebilirsiniz. (Şantiye şefi her işi yürütebilir.)',
    });
}
/** Revize onayi da yalniz sefindir (Bolum 4.5 faz alt-durumlari tablosu). */
export function k1_revizeOnaylayabilirMi(job, actor) {
    if (!sefYetkisiVarMi(actor)) {
        return fail({ code: 'K1', message: 'Revize onayını yalnızca şantiye şefi (veya admin) verebilir.' });
    }
    /*
     * ⚠️ AYNI HATA BURADA DA VARDI: `ekip` calisan id'si tutar, `actor.id` User.id'dir.
     *    Ustelik `aktifRevize.ekip` sunucuda SABIT BOS DIZI olarak dolduruluyordu
     *    (05.08.2026'da duzeltildi), yani bu dal iki ayri sebeple birden oluydu.
     */
    const ekip = job.aktifRevize?.ekip ?? [];
    if (iseKatildiMi({ ekip, kesifciId: null }, actor)) {
        return fail({
            code: 'K1',
            message: 'Bu revizeyi siz yaptınız; kendi revizenizi onaylayamazsınız (K1). Onayı başka bir şantiye şefi vermelidir.',
        });
    }
    return OK;
}
/* ------------------------------------------------------------------ */
/* K2 — Tek sert gecit: TESLIM                                          */
/* ------------------------------------------------------------------ */
/**
 * Cagdas: "cok kritik".
 * Yesil kart + kurulu iskele fotosu olmadan is kapanmaz.
 * Dijital checklist YOKTUR — yasal form kagitta dolduruldu, fotografi yeterli (Faz 5).
 *
 * ⚠️ KONTROL FORMU 04.08.2026'DA ZORUNLULUKTAN CIKARILDI (kullanici karari).
 *
 *    Devir dokumani §3.4 K2'yi "Kontrol formu + yesil kart + kurulu iskele"
 *    diye tanimlar ve Cagdas'in notu "cok kritik"tir; bu satir o tanimi
 *    DARALTIYOR. Bilincli bir sapma oldugu icin burada duruyor.
 *
 *    Fotograf yuvasi KALDIRILMADI: form yuklenebilir, arsivde ve kanit
 *    zincirinde yerini korur (K8) — yalnizca teslimi BLOKLAMAZ. Yeşil kart ve
 *    kurulu iskele fotosu sert gecit olmaya devam ediyor; kart asilmadan is
 *    teslim edilemez.
 *
 *    Gerekce sorulursa: kagit kontrol formu teslim aninda her zaman sahada
 *    olmuyor, ofiste kaliyordu — K5'te izin kagidi icin ogrenilen seyin aynisi.
 */
export function k2_teslimFotolariTamMi(job) {
    const violations = [];
    if (photoCount(job, 'yesil_kart') < 1) {
        violations.push({ code: 'K2', message: 'Yeşil kart fotoğrafı eksik.' });
    }
    if (photoCount(job, 'kurulu_iskele') < 1) {
        violations.push({
            code: 'K2',
            message: 'Kurulu iskele fotoğrafı eksik (yeşil kart görünecek şekilde).',
        });
    }
    return violations.length ? { ok: false, violations } : OK;
}
/* ------------------------------------------------------------------ */
/* K3 — Is emri fotosu: soft baslangic, HARD kapanis                    */
/* ------------------------------------------------------------------ */
/** Kagit fotosu olmadan is KAPANAMAZ; sef/formende "todo" olarak durur. */
export function k3_isEmriFotosuVarMi(job) {
    if (!job.isEmriFotoVar) {
        return fail({
            code: 'K3',
            message: 'İş emri kâğıdının fotoğrafı yüklenmeden iş kapatılamaz (İşlerim → todo).',
        });
    }
    return OK;
}
/** Is ACILISI icin gecit YOKTUR — acil durumda kagitsiz is acilabilir (soft baslangic). */
export function k3_acilistaGecitYok() {
    return OK;
}
/* ------------------------------------------------------------------ */
/* K4 — Izin bir kez alinir, yenileme yok                               */
/* ------------------------------------------------------------------ */
/**
 * Izin baştan sona gecerlidir; yenileme akisi YOKTUR.
 * Bir kez set edildikten sonra degistirmek normal bir akis adimi degil,
 * yalniz sefin geriye donuk duzeltmesidir (audit'e islenir).
 */
export function k4_izinDegistirilebilirMi(job, actor) {
    if (job.izin == null)
        return OK; // ilk kez set ediliyor
    if (sefYetkisiVarMi(actor))
        return OK; // sefin geriye donuk duzeltme yetkisi (Faz 7)
    return fail({
        code: 'K4',
        message: 'İzin bir kez alınır ve baştan sona geçerlidir. Değişikliği yalnızca şef yapabilir.',
    });
}
/**
 * SAHA GERCEGI: musteri sozlu izin veriyor. Sistem ENGELLEMEZ; ilerlesin ama
 * kayit dussun. SORUMLULUK TIKLAYANDADIR ve override orani yonetime
 * raporlanacak degerli bir metriktir.
 *
 * ⚠️ KURULUM BASLANGICI ARTIK IZNE BAGLI DEGIL (musteri karari 03.08.2026:
 *    *"izin konusu cevapsiz birakilabilir. yeter ki kontrol teslimi onu
 *    yuklemeden bitirtmesin."*). Once izin sorusunun CEVAPLANMASI sarti
 *    vardi — cevaplamayan kisi ya "alindi" ya da "override" demek zorundaydi.
 *    Sahada bu, kagit henuz elde yokken kullaniciyi ikisinden birini
 *    UYDURMAYA zorluyordu; ikisi de yanlis kayit uretiyordu.
 *
 *    Kapi tek yerde: TESLIM (`k5_izinFotosuVarMi`). Orada cevapsizlik da
 *    kabul edilmez — ya kagidin fotografi vardir ya "alamadim" kaydi.
 *
 * Bu fonksiyon `kurulum_basladi` gecisinden KALDIRILDI; override akisi
 * yasiyor (izin adimindaki link `/izin` ucuna `durum: 'override'` yazar).
 */
export function k5_kurulumBaslatilabilirMi() {
    return OK;
}
/** Override tiklandiginda yazilacak izin degeri. */
export function k5_izinDegeri(girdi, mevcut) {
    if (mevcut != null)
        return mevcut;
    return girdi.overrideOnayi ? 'override' : 'alindi';
}
/* ------------------------------------------------------------------ */
/* K6 — FIFO                                                            */
/* ------------------------------------------------------------------ */
/**
 * Ilk gelen ilk islenir. Manuel siralama YOKTUR.
 * Prototipte sistem yanlislikla LIFO calisiyordu (yeni is en uste dusuyordu) — v7'de duzeltildi.
 *
 * ⚠️ ONCELIK ALANI ARTIK VAR, AMA SIRALAMAYA GIRMEZ (kullanici karari 09.10.2026).
 *    Kuralin ilk hali "oncelik alani yok" idi (devir dokumani §3.4, §9). SOCAR'in
 *    SAP siparisi 1-6 arasi bir oncelik tasiyor; musteri *"sistemdeki sira gelis
 *    sirasina gore devam etsin, ama filtre uygulanabilir olsun"* dedi. Kural bu
 *    yuzden daraltildi: oncelik BILGI ve FILTRE alanidir, sirayi DEGISTIRMEZ.
 *    Bu fonksiyon oncelige bakmaz ve bakmamali.
 */
export function k6_fifoSirala(items) {
    return [...items].sort((a, b) => a.olusturmaZamani.getTime() - b.olusturmaZamani.getTime());
}
/**
 * Her fotograf bir IS + ADIM + TARIH/SAAT'e baglidir. Yetim foto REDDEDILIR (T3).
 * Gunluk rapordaki FOTO NO kanit zincirinin dogrudan karsiligi.
 */
export function k8_fotoBaglantisiGecerliMi(input) {
    const violations = [];
    if (!input.jobId) {
        violations.push({ code: 'K8', message: 'Fotoğraf bir işe bağlı olmalı — başıboş foto kabul edilmez.' });
    }
    if (!input.step) {
        violations.push({ code: 'K8', message: 'Fotoğraf bir adıma bağlı olmalı.' });
    }
    if (!input.cekilmeZamani) {
        violations.push({ code: 'K8', message: 'Fotoğrafın çekilme tarih ve saati zorunludur.' });
    }
    if (input.step && input.phaseType && PHOTO_STEP_PHASE[input.step] !== input.phaseType) {
        violations.push({
            code: 'K8',
            message: `"${input.step}" adımı "${input.phaseType}" fazına ait değil.`,
        });
    }
    return violations.length ? { ok: false, violations } : OK;
}
/* ------------------------------------------------------------------ */
/* K9 — Sabit listeleri yalniz sef (admin) yonetir                      */
/* ------------------------------------------------------------------ */
/** Formen/ekip basi yeni unite/kisi EKLEYEMEZ. Gerekce: DB kirlenmesini onleme. */
export function k9_tanimYonetebilirMi(actor) {
    if (!sefYetkisiVarMi(actor)) {
        return fail({
            code: 'K9',
            message: 'Üniteler, ilgili kişiler ve çalışan listesini yalnızca şantiye şefi yönetebilir.',
        });
    }
    return OK;
}
/* ------------------------------------------------------------------ */
/* K11 — Bir is emri -> birden cok iskele                               */
/* ------------------------------------------------------------------ */
/**
 * Coklu olcu mekanizmasiyla karsilanir; AYRI IS ACILMAZ.
 * Tek is -> tek "kuruldu" maili.
 */
export function k11_olculerGecerliMi(olculer) {
    if (olculer.length === 0)
        return OK; // kesif oncesi bos olabilir ("keşifte")
    /*
     * ⚠️ GECERLILIK TIPE GORE DEGISIR. `kapama` dairesel bir alandir; en/boy
     *    YOKTUR, yaricap vardir. Hepsinden en/boy istemek, kapama satirini
     *    hic kaydedilemez yapardi.
     */
    for (const o of olculer) {
        const tip = olcuTipi(o);
        if (tip === 'kapama') {
            if (!((o.yaricap ?? 0) > 0)) {
                return fail({ code: 'K11', message: 'Kapama ölçüsünde yarıçap sıfırdan büyük olmalı.' });
            }
            continue;
        }
        if (!((o.en ?? 0) > 0) || !((o.boy ?? 0) > 0)) {
            return fail({ code: 'K11', message: 'Her ölçü satırında en ve boy sıfırdan büyük olmalı.' });
        }
        if (tip === 'normal' && !((o.yuk ?? 0) > 0)) {
            return fail({
                code: 'K11',
                message: 'Normal (hacimli) iskelede yükseklik sıfırdan büyük olmalı.',
            });
        }
    }
    return OK;
}
/* ------------------------------------------------------------------ */
/* K5 — imzali izin kagidinin fotografi (TESLIM gecidinde)              */
/* ------------------------------------------------------------------ */
/**
 * "IZIN ALINDI" DENMISSE KAGIDIN FOTOGRAFI TESLIMDEN ONCE YUKLENMIS OLMALI.
 *
 * ⚠️ KAPI TESLIMDE, "Ben Kurdum"da DEGIL (musteri karari 03.08.2026).
 *    Once kurulum kapanisina konmustu; kaydirildi. Gerekce: kurulumu kapatan
 *    SAHA EKIBI, izin kagidi ise ofis/ISG tarafinda kalabiliyor. Kapi orada
 *    dururken, kagidi elinde olmayan usta isini bitirdigini SOYLEYEMIYORDU
 *    ve is sahada asili kaliyordu. Teslim zaten sefin verdigi tek sert gecit
 *    (K1+K2+K3) — eksik kagit orada, kagida ULASABILEN kisinin onunde cikar.
 *
 * ⚠️ IZIN ADIMI TAMAMEN CEVAPSIZ BIRAKILABILIR (03.08.2026). Ne "alindi"
 *    demek ne fotograf yuklemek kurulumu bekletir; saha kagit gelmeden de
 *    calisir. Ama is TESLIM EDILIRKEN o kagit ortada olmali —
 *    "izin alindi" demis ama gosterememis bir is, kanit zincirinde bosluktur.
 *    Teslim geri alinamaz; bosluk kalici olur.
 *
 * ⚠️ OVERRIDE MUAF. K5'te kisi "izni ALAMADIM, yine de basliyorum" demis ve
 *    gerekcesi kayda gecmistir; olmayan bir kagidin fotografini istemek o
 *    akisi kilitlerdi. Sorumluluk zaten tiklayanda (K5).
 *
 * ⚠️ TIP DAR TUTULDU (`Pick<...>`) ki ARAYUZ DE BU FONKSIYONU CAGIRSIN.
 *    Once arayuz kurali kendi yazmisti (`izin === 'alindi' && foto yok`) ve
 *    kural sunucuda genisleyince (cevapsizlik da reddedilir oldu) ikisi
 *    AYRISTI: dugme acik gorunuyor, sunucu reddediyordu. CLAUDE.md'nin
 *    "ayni kural iki yerde ayri yazilmaz" ilkesinin bedeli tam olarak buydu.
 */
export function k5_izinFotosuVarMi(job) {
    /*
     * ⚠️ IZIN GEREKMEYEN BOLGE (SOCAR dis projeleri, 09.10.2026). Cevap 5: *"Dis
     *    projeler icin bu adim atlanabilir."* Orada imzali izin kagidi diye bir
     *    belge yoktur; istemek isi teslimde kilitlerdi. Bilgi unitede durur.
     */
    if (job.izinGerekmez)
        return OK;
    // Override: kisi "izni ALAMADIM" demis, gerekcesi kayda gecmistir.
    if (job.izin === 'override')
        return OK;
    if (photoCount(job, 'izin') > 0)
        return OK;
    return fail({
        code: 'K5',
        message: job.izin === 'alindi'
            ? '"İzin alındı" işaretli ama imzalı izin kâğıdının fotoğrafı yok. Teslimden önce yükleyin.'
            : 'İmzalı izin kâğıdının fotoğrafı yüklenmeden teslim edilemez. Kâğıt hiç alınamadıysa izin adımından "izni alamadım" kaydını düşün.',
    });
}
/* ------------------------------------------------------------------ */
/* REVIZE — bitirme kapisi                                              */
/* ------------------------------------------------------------------ */
/**
 * "Revizeyi bitirdik" FOTOGRAFSIZ denemez (musteri karari 31.07.2026).
 *
 * Gerekce K2'nin aynisi: revize iskelenin halini DEGISTIRIR. Sefin onaylayacagi
 * sey, revize sonrasi durumdur; fotograf olmadan onay "bana oyle dediler"e
 * dayanir. Revize fotografi eskiden "istege bagli"ydi ve pratikte hic
 * yuklenmiyordu.
 *
 * ⚠️ Bu bir SERT gecittir ama TESLIM gecidi degildir: revize onayi K1 ile
 *    zaten sefte. Buradaki kural yalnizca "onaya fotografsiz gitme" der.
 */
export function revizeBitirilebilirMi(job) {
    if (job.photos.filter((p) => p.step === 'revize' && !p.gecersiz).length < 1) {
        return fail({
            code: 'K2',
            message: 'Revize fotoğrafı olmadan bitirilemez — şef onayı fotoğrafa bakarak verilir.',
        });
    }
    return OK;
}
/* ------------------------------------------------------------------ */
/* K12 — Hurda ayri prosestir                                           */
/* ------------------------------------------------------------------ */
/** Sokum sonrasi malzeme yalniz "depoya iade" veya "bolgede birakildi" olabilir. Hurda YOK. */
export function k12_malzemeAkibetiGecerliMi(akibet) {
    if (akibet === 'depoya_iade' || akibet === 'bolgede_birakildi')
        return OK;
    return fail({
        code: 'K12',
        message: 'Hurda ayrı bir prosestir, sökümün parçası değildir. Malzeme depoya iade veya bölgede bırakıldı olarak kaydedilir.',
    });
}
/* ------------------------------------------------------------------ */
/* Birlesik gecitler                                                    */
/* ------------------------------------------------------------------ */
/**
 * TESLIM GECIDI — sistemdeki TEK sert gecit.
 * K1 (onaylayan != kuran) + K2 (3 foto) + K3 (is emri fotosu) + nihai metraj.
 */
export function teslimEdilebilirMi(job, actor) {
    const nihaiMetraj = job.nihaiOnayli
        ? OK
        : fail({
            code: 'STATE',
            message: 'Nihai boyut (son ölçüler) onaylanmadan teslim yapılamaz.',
        });
    return combine(k1_onaylayabilirMi(job, actor), k2_teslimFotolariTamMi(job), k3_isEmriFotosuVarMi(job), k5_izinFotosuVarMi(job), nihaiMetraj);
}
/** Sokum kapanis gecidi: bos alan fotosu ZORUNLU + is emri fotosu (K3). */
/**
 * SOKUM EMRI ALINIRKEN IS EMRI KAGIDI ORTADA OLMALI.
 *
 * ⚠️ CIKMAZ SOKAK ONLEMI (musteri istegi 03.08.2026). "Emri aldim" adiminda
 *    iki yol var: var olan is emriyle devam et / yeni sokum is emri fotosu
 *    yukle. Ama is emri fotosu HIC OLMAYAN eski bir iste "var olani kullan"
 *    yolu bos bir vaat: ortada kullanilacak kagit yok. Daha kotusu, ekip
 *    sokumu bastan sona yapip KAPANISTA K3'e carpiyordu — is emri fotosu
 *    olmadan is kapanmiyor. Emek harcanmis, is kapanmiyor.
 *
 * ⚠️ ISTENEN FOTOGRAF `is_emri`, `sokum_is_emri` DEGIL. Sokum kagidi ayri bir
 *    belge (§6.6) ve `isEmriFotoVar` bayragini KURMAZ; yalniz onu yukletmek
 *    ciktmazi cozmezdi, yalnizca sonraya oteler.
 *
 * Bu bir K3 uygulamasidir: "soft baslangic, hard kapanis" — kapanis kapisi
 * yerinde duruyor, buradaki kontrol o kapiyi ekibe ONCEDEN gosteriyor.
 */
export function sokumEmriAlinabilirMi(job) {
    if (job.isEmriFotoVar)
        return OK;
    return fail({
        code: 'K3',
        message: 'Bu işin iş emri kâğıdı fotoğrafı yok. Söküm bu fotoğraf olmadan kapanamaz (K3) — önce yükleyin.',
    });
}
/**
 * SOKUM EMRI GERI ALINABILIR MI — iskele yerinde kalir, is `ayakta`ya doner.
 *
 * Nuh 26.08.2026: *"sokum islemi baslatildiktan sonra yanlislikla baslatilan
 * veya karistirilan bir sokum isinin iptal edilmesi mumkun olmuyor."*
 *
 * ⚠️ SINIR BOS ALAN FOTOGRAFIDIR, sure ya da adim sayisi DEGIL. O fotograf
 *    cekildiyse iskele FIILEN INMISTIR; "geri aldim" demek sahadaki gercege
 *    aykiri bir kayit uretir ve K8 kanit zinciri o yalani sadakatle korur.
 *    Foto cekilmeden once ise ortada yalnizca bir EMIR vardir ve emir geri
 *    alinabilir.
 *
 * ⚠️ Sokum tikleri (basla/devam) geri almayi ENGELLEMEZ. Ekip "basladim"
 *    demis ama daha malzeme inmemis olabilir; asil olcut fizikseldir.
 *
 * ⚠️ YALNIZ SEF YETKISI. Sokum emrini acan sef/muhendistir (Bolum 6.3);
 *    geri almak da ayni mercinin isi. Ustanin araci 10 dakikalik Geri Al.
 */
export function sokumGeriAlinabilirMi(job, actor) {
    if (!sefYetkisiVarMi(actor)) {
        return fail({
            code: 'YETKI',
            message: 'Söküm emrini yalnızca şantiye şefi ya da saha mühendisi geri alabilir.',
        });
    }
    if (job.sokum?.bosAlanFotoVar) {
        return fail({
            code: 'STATE',
            message: 'Boş alan fotoğrafı çekilmiş — iskele sökülmüştür, söküm geri alınamaz. ' +
                'Yeniden kurulacaksa yeni bir iş açın.',
        });
    }
    return OK;
}
export function sokumKapatilabilirMi(job) {
    const bosAlan = job.sokum?.bosAlanFotoVar
        ? OK
        : fail({ code: 'STATE', message: 'Alan temizliği için boş alan fotoğrafı zorunludur.' });
    return combine(bosAlan, k3_isEmriFotosuVarMi(job));
}
/* ------------------------------------------------------------------ */
/* KULLANICI VE ROL YONETIMI                                            */
/*                                                                      */
/* Devir dokumaninda super-admin YOKTUR (Bolum 3.1 organizasyon semasi   */
/* operasyoneldir). Bu kurallar dokumanin uzerine, sonradan gelen acik   */
/* bir istekle eklenmistir; yine de dokumanin iki ilkesine uyar:         */
/*   - K9'un gerekcesi: "DB kirlenmesini onleme" -> hesap acmak da       */
/*     serbest degildir, yetkili kisi yapar.                             */
/*   - T5: "rol bazli yetki ama KATI OLMAMALI".                          */
/* ------------------------------------------------------------------ */
/** Hesap acabilen/kapatabilenler: admin ve sef. */
export function kullaniciYonetebilirMi(actor) {
    if (!sefYetkisiVarMi(actor)) {
        return fail({
            code: 'YETKI',
            message: 'Kullanıcı ekleme/çıkarma yetkisi yalnızca admin ve şantiye şefindedir.',
        });
    }
    return OK;
}
/**
 * YETKI YUKSELTME KAPISI.
 *
 * Bir kullanici, KENDINDEN yetkili bir hesap olusturamaz/atayamaz.
 * Aksi halde sef kendine admin hesabi acip sinirsiz yetki alir — klasik
 * privilege escalation. Sef yalnizca saha rolleri acabilir.
 */
export function rolAtayabilirMi(actor, hedefRol, hedefSistemRol) {
    const yonetim = kullaniciYonetebilirMi(actor);
    if (!yonetim.ok)
        return yonetim;
    /*
     * ⚠️ MUSTERI YETKILISI ADMIN OLAMAZ — sirasi onemli, admin dalindan ONCE.
     *    `sefYetkisiVarMi` musteri rolunu zaten fail-closed ele aliyor ama bu
     *    kombinasyon hic olusmamali: musteri tarafindaki bir kisiye sistem
     *    yoneticiligi vermek, kilit/ayar/kullanici ekranlarini ona acmak olurdu.
     */
    if (hedefRol === 'musteri' && hedefSistemRol === 'admin') {
        return fail({
            code: 'YETKI',
            message: 'Müşteri yetkilisine admin rolü verilemez.',
        });
    }
    if (actor.sistemRol === 'admin')
        return OK; // admin her rolu atayabilir
    // Buradan asagisi: actor bir SEF (admin degil).
    if (hedefSistemRol === 'admin') {
        return fail({
            code: 'YETKI',
            message: 'Admin hesabını yalnızca bir admin oluşturabilir.',
        });
    }
    if (hedefRol === 'sef') {
        return fail({
            code: 'YETKI',
            message: 'Şantiye şefi rolünü yalnızca admin atayabilir.',
        });
    }
    return OK;
}
/**
 * Hesap kapatma.
 *
 * Iki kapi:
 *  1) KENDINI KAPATAMAZ — kaza eseri kilitlenmeyi onler.
 *  2) SON ADMIN KAPATILAMAZ — sistemde admin kalmazsa kimse kullanici acamaz
 *     ve kurtarma yolu yalnizca ortam degiskeni + yeniden deploy olur.
 */
export function kullaniciSilebilirMi(actor, baglam) {
    const yonetim = kullaniciYonetebilirMi(actor);
    if (!yonetim.ok)
        return yonetim;
    if (baglam.hedefKullaniciId === actor.id) {
        return fail({ code: 'YETKI', message: 'Kendi hesabınızı kapatamazsınız.' });
    }
    if (baglam.hedefSistemRol === 'admin' && actor.sistemRol !== 'admin') {
        return fail({ code: 'YETKI', message: 'Admin hesabını yalnızca bir admin kapatabilir.' });
    }
    if (baglam.hedefSistemRol === 'admin' && baglam.aktifAdminSayisi <= 1) {
        return fail({
            code: 'YETKI',
            message: 'Sistemdeki son admin hesabı kapatılamaz — kimse kullanıcı yönetemez hale gelir.',
        });
    }
    return OK;
}
/** Kimse kendi sistem rolunu degistiremez — self-escalation kapisi. */
export function sistemRolDegistirebilirMi(actor, hedefKullaniciId) {
    if (actor.sistemRol !== 'admin') {
        return fail({ code: 'YETKI', message: 'Sistem rolünü yalnızca admin değiştirebilir.' });
    }
    if (hedefKullaniciId === actor.id) {
        return fail({
            code: 'YETKI',
            message: 'Kendi sistem rolünüzü değiştiremezsiniz. Başka bir admin yapmalı.',
        });
    }
    return OK;
}
/* ------------------------------------------------------------------ */
/* GIRIS — telefon + parola                                            */
/* ------------------------------------------------------------------ */
/**
 * GIRIS KAPISI.
 *
 * Harici saglayici yok; parolayi dogrulamak API katmaninin isi (argon2).
 * Burada dogrulanan, parola DOGRU OLSA BILE girisi engelleyen kosullardir.
 *
 * Kaba kuvvet korumasi: 5 basarisiz denemeden sonra hesap gecici kilitlenir.
 * Sahada yanlis parola yazmak siktir; kalici kilit destek yuku yaratir, bu
 * yuzden SURELI kilit (varsayilan 15 dk) ve sef/admin elle acabilir.
 */
export const MAKS_BASARISIZ_GIRIS = 5;
export const KILIT_SURESI_DK = 15;
export function girisiDegerlendir(baglam) {
    const red = (message, kilitlenmeliMi = false) => ({
        ok: false,
        violations: [{ code: 'YETKI', message }],
        kilitlenmeliMi,
    });
    const k = baglam.kullanici;
    // GUVENLIK: kullanici yoksa da parola yanlissa da AYNI mesaj doner.
    // Farkli mesaj vermek, hangi telefonlarin sistemde kayitli oldugunu sizdirir.
    if (!k)
        return red('Telefon numarası veya şifre hatalı.');
    if (k.kilitliAt && k.kilitliAt.getTime() > baglam.simdi.getTime()) {
        const kalanDk = Math.ceil((k.kilitliAt.getTime() - baglam.simdi.getTime()) / 60_000);
        return red(`Çok fazla hatalı deneme. ${kalanDk} dakika sonra tekrar deneyin.`);
    }
    if (!baglam.parolaDogru) {
        const yeniSayi = k.basarisizGirisSayisi + 1;
        return red('Telefon numarası veya şifre hatalı.', yeniSayi >= MAKS_BASARISIZ_GIRIS);
    }
    // Parola dogru ama hesap kapatilmis: BURADA acik mesaj veriyoruz, cunku
    // kimligini ispatlamis birine "hesabin kapali" demek bilgi sizdirmaz.
    if (!k.aktif)
        return red('Hesabınız kapatılmış. Yöneticinize başvurun.');
    return { ok: true, sistemRol: k.sistemRol };
}
/**
 * BOOTSTRAP.
 *
 * Ilk admin bir yerden gelmeli. Ortam degiskeninden okunan telefon+parola ile
 * acilista bir admin hesabi olusturulur (varsa dokunulmaz).
 * Ayni zamanda "tum adminleri kaybettik" durumundan tek cikis yoludur.
 */
export function bootstrapAdminGerekliMi(mevcutAdminSayisi) {
    return mevcutAdminSayisi === 0;
}
/* ------------------------------------------------------------------ */
/* TEMSIL (impersonation) — admin test paneli                          */
/* ------------------------------------------------------------------ */
/** Sicil numarasi ayrimi: gercek saha kadrosu 1–8999, sistem/test 9000+. */
export const SICIL_SISTEM_BASLANGIC = 9000;
export function sicilSistemMi(sicilNo) {
    return sicilNo >= SICIL_SISTEM_BASLANGIC;
}
/**
 * Bir sonraki sicil numarasi.
 * Gercek calisanlar 1'den, sistem/test hesaplari 9000'den ilerler — boylece
 * iki kume ekranda ve raporda hicbir zaman karismaz.
 */
export function sonrakiSicilNo(mevcutSiciller, tip) {
    const alan = tip === 'sistem'
        ? mevcutSiciller.filter(sicilSistemMi)
        : mevcutSiciller.filter((n) => !sicilSistemMi(n) && n > 0);
    if (alan.length === 0)
        return tip === 'sistem' ? SICIL_SISTEM_BASLANGIC : 1;
    return Math.max(...alan) + 1;
}
/**
 * Temsilin KAPSAMI — admin panelindeki "Ayarlar" sekmesinden yonetilir.
 *
 * Temsil bir YETKI YUKSELTME araci degildir: admin, temsil ettigi kisinin
 * yetkilerini aynen ustlenir (bkz. ActorContext.gercekKullaniciId). Bu ayar
 * yetkiyi degil, temsilin KIMLERE uygulanabilecegini belirler.
 */
export const TEMSIL_MODLARI = ['kapali', 'sadece_test', 'tum_kullanicilar'];
/** Test donemi varsayilani. Pilot oncesi `sadece_test` (ya da `kapali`) yapilacak. */
export const VARSAYILAN_TEMSIL_MODU = 'tum_kullanicilar';
/**
 * Kimin yerine gecilebilir?
 *
 *   - YALNIZ admin temsil edebilir.
 *   - Kendi kendini temsil edemez (anlamsiz, ayrica durum makinesini karistirir).
 *   - Baska bir ADMIN'in yerine gecilemez: yetki yukseltmenin dolayli yolu olurdu.
 *   - TEST hesabi  -> tam yetki (gercek kisi degil, kaydi delil degil)
 *   - GERCEK hesap -> SALT OKUNUR (ekranini gor, adina islem yapma)
 */
export function temsilEdebilirMi(actor, hedef, modu = VARSAYILAN_TEMSIL_MODU) {
    const red = (message) => ({
        ok: false,
        violations: [{ code: 'YETKI', message }],
    });
    if (actor.sistemRol !== 'admin') {
        return red('Başka bir kullanıcının yerine yalnızca admin geçebilir.');
    }
    // Zaten temsil altindaysa zincirleme temsil yok — gercek kimlik izlenemez hale gelir.
    if (actor.gercekKullaniciId) {
        return red('Temsil sırasında başka bir kullanıcıya geçilemez. Önce kendinize dönün.');
    }
    if (hedef.id === actor.id) {
        return red('Kendi yerinize geçemezsiniz.');
    }
    if (!hedef.aktif) {
        return red('Kapatılmış bir hesabın yerine geçilemez.');
    }
    if (hedef.sistemRol === 'admin') {
        return red('Başka bir adminin yerine geçilemez.');
    }
    if (modu === 'kapali') {
        return red('Kullanıcı geçişi kapalı. Admin panelindeki Ayarlar sekmesinden açılabilir.');
    }
    if (modu === 'sadece_test' && !hedef.testKullanicisi) {
        return red('Şu anda yalnızca test hesaplarına geçilebilir (Ayarlar → kullanıcı geçişi).');
    }
    return { ok: true };
}
/**
 * Temsil ayarini degistirme — yalniz admin, ve KENDISI de iz kaydina yazilir.
 * "Bu ayar ne zaman acildi/kapandi" sorusu denetimin ilk soracagi sorulardan biridir.
 */
export function temsilModuDegistirebilirMi(actor) {
    if (actor.sistemRol !== 'admin') {
        return fail({ code: 'YETKI', message: 'Bu ayarı yalnızca admin değiştirebilir.' });
    }
    // Temsil altindayken bu ayari degistirmek dongusel bir yetki tuzagidir.
    if (actor.gercekKullaniciId) {
        return fail({
            code: 'YETKI',
            message: 'Temsil sırasında bu ayar değiştirilemez. Önce kendinize dönün.',
        });
    }
    return OK;
}
/** Test hesabi olusturma — yalniz admin. */
export function testKullanicisiOlusturabilirMi(actor) {
    if (actor.sistemRol !== 'admin') {
        return fail({ code: 'YETKI', message: 'Test kullanıcısı oluşturma yetkisi yalnızca adminde.' });
    }
    // Temsil sirasinda admin yetkisi zaten yoktur (sistemRol hedefinkidir),
    // dolayisiyla buraya temsil altinda hic gelinemez. Yine de acik birakmayalim.
    if (actor.gercekKullaniciId) {
        return fail({
            code: 'YETKI',
            message: 'Kullanıcı geçişi sırasında test kullanıcısı oluşturulamaz. Önce kendinize dönün.',
        });
    }
    return OK;
}
/**
 * Iz kaydina yazilacak kimlik ciftini uretir.
 * Temsil altinda `kullaniciId` temsil edileni, `gercekKullaniciId` admini gosterir;
 * denetim "kim tikladi" sorusunu her zaman cevaplayabilir (K5).
 */
export function izKaydiKimlik(actor) {
    return {
        kullaniciId: actor.id,
        gercekKullaniciId: actor.gercekKullaniciId ?? null,
        temsilMi: actor.gercekKullaniciId != null,
    };
}
/* ------------------------------------------------------------------ */
/* GECMISE DONUK DUZELTME — Bolum 6.3                                   */
/* ------------------------------------------------------------------ */
/**
 * Duzeltilebilir alanlar — isin zaman cizgisindeki SIRASIYLA.
 *
 * Sira onemlidir: formenin duzeltme yetkisi "bir onceki adim" ile sinirli
 * ve o sinir bu dizinin indisinden hesaplaniyor (bkz. `duzeltilebilirMi`).
 */
export const DUZELTME_ALANLARI = [
    'isEmri', // 0 — kunye: is emri no, tanim
    'kesif', // 1 — olculer, kesif notu
    'izinMalzeme', // 2 — izin durumu/notu, malzeme sevk
    'ekip', // 3 — kurulum ekibi + ekip basi
    'nihaiOlcu', // 4 — nihai metraj, iskele onay no
];
/**
 * Isin SU ANDA hangi adimda oldugu — `DUZELTME_ALANLARI` indisiyle.
 *
 * "Bir onceki adim" = bu sayinin bir eksigi. Yani `kontrol` durumundaki bir
 * iste (indis 4) formen ekibi (indis 3) duzeltebilir, keşfi (indis 1) DUZELTEMEZ.
 *
 * ⚠️ `kurulum` ile `hazirlik` AYNI indistedir (3): ekip kurulum baslarken
 *    giriliyor, yani saha calismasi surerken ekip hala GUNCEL adimdir.
 */
const DURUM_ADIMI = {
    giris: 1, // kesif bekliyor
    kesif: 2, // izin & malzeme bekleniyor
    hazirlik: 3, // kuruluma hazir
    kurulum: 3, // ekip girilmis, saha calisiyor
    kontrol: 4, // "ben kurdum" dendi, nihai olcu/onay sirada
    ayakta: 5, // teslim edildi — tum adimlar geride
    revize: 5,
    sokum: 6,
    kapali: 6,
    ertelendi: 3, // yeniden planlanacak; hazirlik adiminda donmus sayilir
    iptal: 6,
};
/**
 * Bir alanin bu is icin duzeltilip duzeltilemeyecegi.
 *
 * Yetki: YALNIZ SEF/ADMIN. Dokuman bunu "sefin gecmise donuk duzeltme yetkisi"
 * diye tanimliyor; K4 de ayni sonuca variyor ("degisikligi yalnizca sef yapar").
 *
 * Zamanlama, §6.3 tablosundan BIREBIR:
 *   - Is emri (kagit) : "Akis surerken evet; KAPANDIKTAN SONRA HAYIR"
 *   - Kesif           : "KAPALI ISTE BILE EVET — audit'e islenir" (ozel istek)
 *   - Izin & malzeme  : "Evet"
 *
 * ⚠️ Kesif'in kapali iste bile acik olmasi tuhaf gorunebilir ama kasitli:
 *    olcu/metraj gecmise donuk raporlamayi besliyor, yanlis kalirsa kumulatif
 *    rakamlar kalici olarak bozuk kalir.
 */
export function duzeltilebilirMi(job, alan, actor) {
    const sef = sefYetkisiVarMi(actor);
    const formen = actor.role === 'formen';
    if (!sef && !formen) {
        return fail({
            code: 'K4',
            message: 'Geçmişe dönük düzeltmeyi yalnızca şef, mühendis ve formen yapabilir.',
        });
    }
    /*
     * FORMENIN PENCERESI: YALNIZ BIR ONCEKI ADIM.
     *
     * Musteri istegi (03.08.2026): *"bir onceki adimda duzenleme yapabilmeli.
     * ama eger sonraki adim da gectiyse, iki adim gerisinde duzenleme
     * yapamamali."*
     *
     * ⚠️ Sef bu pencereye TABI DEGILDIR — §6.3 sefe suresiz duzeltme yetkisi
     *    verir ("kesif: KAPALI ISTE BILE EVET") ve o yetki burada daraltilmiyor.
     *    Formen icin pencere dar tutuldu cunku amac "az once yanlis girdim"i
     *    duzeltmek; gecmise donuk toplu duzeltme sefin isi.
     */
    if (!sef) {
        // Bilinmeyen durumda KAPALI tarafa dusuyoruz: isin nerede oldugunu
        // bilmiyorsak "bir onceki adim" penceresi de hesaplanamaz.
        const simdi = DURUM_ADIMI[job.durum];
        if (simdi == null) {
            return fail({ code: 'K4', message: 'İşin adımı belirlenemedi — düzeltmeyi şef yapmalı.' });
        }
        const hedef = DUZELTME_ALANLARI.indexOf(alan);
        if (hedef !== simdi && hedef !== simdi - 1) {
            return fail({
                code: 'K4',
                message: hedef < simdi - 1
                    ? 'Bu adım geride kaldı — formen yalnız bir önceki adımı düzeltebilir. Şefe iletin.'
                    : 'Bu adıma henüz gelinmedi.',
            });
        }
    }
    if (alan === 'isEmri' && job.durum === 'kapali') {
        return fail({
            code: 'K3',
            message: 'İş kapandıktan sonra iş emri künyesi değiştirilemez.',
        });
    }
    return OK;
}
/* ------------------------------------------------------------------ */
/* FOTOGRAF GECERSIZ ISARETLEME — "yanlis kagidi yukledim"              */
/* ------------------------------------------------------------------ */
/**
 * Bir fotografin GECERSIZ isaretlenip isaretlenemeyecegi.
 *
 * ⚠️ BU BIR SILME DEGIL. Fotograf yerinde kalir (K8: silinemez), yalniz
 *    "sayilmaz" hale gelir: musteriye giden ciktilara girmez, K3/K5
 *    gecitlerinde sayilmaz, ic ekranlarda ustu cizili gorunur.
 *
 * ⚠️ USTA YAPAMAZ. Onun araci 10 dakikalik Geri Al'dir ve fotograf zaten onun
 *    kapsaminda degil. Yanlis kagit "yanlis tik" degildir: sonucu bir belgenin
 *    yanlis ise baglanmasidir ve duzeltmesi de duzeltme yetkisi isteyen bir
 *    istir (Bolum 6.3 — sef · muhendis · formen).
 *
 * ⚠️ KAPANMIS ISTE HAYIR. Is kapandiginda kanit zinciri muhurlenir; ayni sinir
 *    is emri kunyesi duzeltmesinde de var ("KAPANDIKTAN SONRA HAYIR").
 *    Kapanmis bir iste yanlis kagit bulunursa yol, kaydin kendisi degil
 *    denetimdir.
 *
 * GEREKCE ZORUNLU ve bu sunucu tarafinda da dogrulanir: gerekcesiz bir
 * gecersizlik, denetimde HATADAN ayirt edilemez.
 */
export function fotoGecersizIsaretlenebilirMi(job, foto, actor) {
    if (!sefYetkisiVarMi(actor) && actor.role !== 'formen') {
        return fail({
            code: 'K8',
            message: 'Fotoğrafı geçersiz işaretlemeyi yalnızca şef, mühendis ve formen yapabilir.',
        });
    }
    if (foto.gecersiz) {
        return fail({ code: 'K8', message: 'Bu fotoğraf zaten geçersiz işaretli.' });
    }
    if (job.durum === 'kapali' || job.durum === 'iptal') {
        return fail({
            code: 'K8',
            message: 'İş kapandı — kanıt zinciri mühürlüdür, fotoğraf geçersiz işaretlenemez.',
        });
    }
    return OK;
}
/* ------------------------------------------------------------------ */
/* GERI AL — "yanlis tik" (prototip `snap`/`doUndo`)                    */
/* ------------------------------------------------------------------ */
/**
 * Saha kullanicisi eldivenli ve aceleci; yanlis tik ATILIYOR.
 *
 * ⚠️ GERI ALMAK, SILMEK DEGILDIR. Iz kaydi degismezdir (T4): geri alma da
 *    AYRI BIR SATIR olarak eklenir — prototipin metniyle "↩ Yanlis tik geri
 *    alindi (iz kaydinda tutulur)".
 *
 * ⚠️ HER ADIM GERI ALINAMAZ. Bilincli olarak dar bir kume:
 *    - TESLIM geri alinamaz: K2 gecidi gecilmis, musteriye mail gitmis,
 *      yesil kart asilmistir. Bunun geri donusu "guvenli degil" akisidir —
 *      sessizce silinmesi degil.
 *    - SOKUM KAPANISI geri alinamaz: is kapanmis, kanit zinciri muhurlenmistir.
 *    - Foto geri alinamaz (K8: fotograflar silinemez).
 */
export const GERI_ALINABILIR = [
    'izin_alindi',
    'izin_override',
    'malzeme_sevk',
    'kurulum_basladi',
    'kurulum_bitti',
];
/**
 * Geri alma penceresi (dakika).
 *
 * Neden sinirli: bu ozellik "yanlis tik" icindir, fikir degistirme araci degil.
 * Saatler sonra gelen bir geri alma, aslinda bir DUZELTMEDIR ve sefin
 * gerekce yazdigi yoldan (Bolum 6.3, "⋮") gecmelidir.
 */
export const GERI_AL_PENCERESI_DK = 10;
export function geriAlinabilirMi(tip, yasDakika, ayniKisiMi, actor) {
    if (!GERI_ALINABILIR.includes(tip)) {
        return fail({
            code: 'STATE',
            message: 'Bu adım geri alınamaz. Düzeltme gerekiyorsa şef "⋮" ile gerekçeli düzeltir.',
        });
    }
    if (yasDakika > GERI_AL_PENCERESI_DK) {
        return fail({
            code: 'STATE',
            message: `Geri alma süresi doldu (${GERI_AL_PENCERESI_DK} dk). Şef "⋮" ile gerekçeli düzeltebilir.`,
        });
    }
    // K5 — sorumluluk tiklayanda: kendi tikini kendi geri alir. Sef her tiki alabilir.
    if (!ayniKisiMi && !sefYetkisiVarMi(actor)) {
        return fail({
            code: 'K5',
            message: 'Yalnızca işlemi yapan kişi ya da şef geri alabilir.',
        });
    }
    return OK;
}
//# sourceMappingURL=rules.js.map